import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import inputStyles from '../../../ManagementPages.module.css';
import styles from '../../Patient.module.css';
import { showAlert } from '../../../../components/PerinHealthAlertSlice';
import { normalizeHeightToCm, normalizeWeightToKg, buildHeightState, buildWeightState } from '../../../../utils/accountUtils';

/** Validation aligned with HealthInfo.js: gender required; height/weight valid when provided. */
const validateHealthInfo = (payload) => {
    const errors = {};
    if (!(payload.gender || "").trim()) {
        errors.gender = "Please select a gender.";
    }
    const height = payload.height;
    if (height !== "" && height != null) {
        const h = parseFloat(height);
        if (Number.isNaN(h) || h < 0) {
            errors.height = "Please enter a valid height.";
        }
    }
    const weight = payload.weight;
    if (weight !== "" && weight != null) {
        const w = parseFloat(weight);
        if (Number.isNaN(w) || w < 0) {
            errors.weight = "Please enter a valid weight.";
        }
    }
    return { valid: Object.keys(errors).length === 0, errors };
};

const HealthInfoDetail= ({ setModal, setUpdateFn, setCancelFn }) => {

    const dispatch = useDispatch();
    const patient = useSelector((state)=>state.patient);
    const {firstName, lastName, healthInfo, preferences, gender} = patient;
    const isMetric = preferences.unitType === "Metric";
    const today = new Date().toLocaleDateString('fr-ca');
    const [showUpdateButtons, setShowUpdateButtons] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const dropdowns = useSelector((state) => state.dropdowns);


    const [weight, setWeight] = useState(() =>
        buildWeightState(isMetric, healthInfo.weight)
    );

   const [height, setHeight] = useState(() =>
        buildHeightState(isMetric, healthInfo.height)
    );

    const handleWeightBlur = () => {
        if (weight === '' || /^\d+$/.test(weight)) {
            const numericValue = weight === '' ? 0 : Number(weight);
            let metricValue = numericValue;
            if (!isMetric) {
                // convert lb to kg
                metricValue = Number((metricValue * 0.45359237).toFixed(2));
            }
            const tempData = {...updatedInfo};
            tempData["weight"] = metricValue;
            setUpdatedInfo(tempData);
        }
    };

    const handleHeightBlur = () => {
        const metricHeight = normalizeHeightToCm(isMetric, height);
        if (metricHeight === null) {
            return;
        }
        const tempData = { ...updatedInfo, height: metricHeight };
        setUpdatedInfo(tempData);
    };

    const initialState = {
        ...healthInfo,
        dateOfBirth: healthInfo.dateOfBirth ? healthInfo.dateOfBirth.substring(0, 10) : "",
        criticalLevel: healthInfo.criticalLevel || "",
        notes: healthInfo.notes || "",
    };

    const [ updatedInfo, setUpdatedInfo ] = useState(initialState);
    const [count, setCount] = useState(updatedInfo.notes.length);

    const showUpdateButton = () => {
        if (!showUpdateButtons) {
            setShowUpdateButtons(true);
        };
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        const tempData = {...updatedInfo};
        tempData[id] = value;
        if (id==="notes") {
            setCount(value.length);
        };
        setUpdatedInfo(tempData);
        showUpdateButton();
    };

    const handleUpdate = () => {
        // Sync height/weight from local state (in case user didn't blur)
        const metricHeight = normalizeHeightToCm(isMetric, height);
        const syncedHeight = metricHeight === null ? updatedInfo.height : metricHeight;
        const metricWeight = normalizeWeightToKg(isMetric, weight);
        const syncedWeight = metricWeight === null ? updatedInfo.weight : metricWeight;

        const payload = {
            ...updatedInfo,
            height: syncedHeight,
            weight: syncedWeight,
        };

        const { valid, errors } = validateHealthInfo(payload);
        if (!valid) {
            setValidationErrors(errors);
            return;
        }
        setValidationErrors({});

        const updatingData = { ...patient, gender: payload.gender, healthInfo: payload };
        const updateFn = () => {
            dispatch(
                showAlert({
                    header: "Success",
                    message: "Health Info updated",
                    type: 'success',
                })
            );
            setShowUpdateButtons(false);
        };
        setUpdateFn(() => updateFn);
        setCancelFn(() => handleCancel);
        setModal({ title: "Health Info", message: `Update ${firstName} ${lastName}'s health info?`, data: updatingData });
    };

    const handleCancel = () => {
        setUpdatedInfo(initialState);
        setShowUpdateButtons(false);
        setValidationErrors({});
    };

    return (
        <div style={{display:"flex", flexDirection:"column", gap:"15px"}}>
            <div className="input-name"
              style={{paddingTop:"10px"}}>
                Date of Birth
            </div>
            <div className={inputStyles["styled-input-container"]}>
                  <span className="floating-label" >Date of Birth</span>
                  <input 
                    id="dateOfBirth"
                    type="date"
                    max={today}
                    value={updatedInfo.dateOfBirth}
                    onChange={handleChange}
                  ></input>
            </div>
            <div className="input-name"
              style={{paddingTop:"10px"}}>
                Height
            </div>
            {isMetric ?
                <div className={inputStyles["styled-input-container"]}>
                    <input
                        id="cm"
                        type="number"
                        min="0"
                        step="any"
                        style={{ borderColor: validationErrors.height ? '#dc3545' : undefined }}
                        value={height.cm}
                        onChange={(e) => setHeight(prevUserData => ({
                            ...prevUserData,
                            "cm": e.target.value
                        }))}
                        onBlur={() => handleHeightBlur()}
                    ></input>
                    <label className={height.cm && inputStyles['filled']}>
                    cm
                    </label>
                </div>
              :
              <div className="input-block" style={{width: "300px", marginBottom:"0"}}>
                <div className={inputStyles["styled-input-container"]}>
                    <input
                        id="feet"
                        type="number"
                        min="0"
                        max="8"
                        style={{ width: "147px", borderColor: validationErrors.height ? '#dc3545' : undefined }}
                        value={height.feet}
                        onChange={(e) => {
                            showUpdateButton();
                            setHeight(prevUserData => ({
                                ...prevUserData,
                                "feet": e.target.value
                            }));
                        }}
                        onBlur={() => handleHeightBlur()}
                    ></input>
                        <label className={height.feet && inputStyles['filled']}>
                        ft
                        </label>
                </div>
                <div className={inputStyles["styled-input-container"]}>
                    <input
                        id="inches"
                        type="number"
                        min="0"
                        max="11"
                        style={{ width: "147px", borderColor: validationErrors.height ? '#dc3545' : undefined }}
                        value={height.inches}
                        onChange={(e) => {
                            showUpdateButton();
                            const raw = e.target.value;
                            if (raw === "" || raw === null || raw === undefined) {
                                setHeight(prev => ({ ...prev, inches: "" }));
                                return;
                            }
                            const num = Number(raw);
                            if (Number.isNaN(num)) return;
                            const clamped = Math.min(11, Math.max(0, Math.round(num)));
                            setHeight(prevUserData => ({
                                ...prevUserData,
                                inches: clamped
                            }));
                        }}
                        onBlur={() => handleHeightBlur()}
                    ></input>
                        <label className={height.inches && inputStyles['filled']}>
                        in
                        </label>
                </div>
              </div>
              
            }
            {validationErrors.height && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '-4px' }}>{validationErrors.height}</div>
            )}
            <div className="input-name">
                Weight
            </div>
            <div className={inputStyles["styled-input-container"]}>
                <input
                    style={{ width: "147px", borderColor: validationErrors.weight ? '#dc3545' : undefined }}
                    type="number"
                    step="any"
                    id="weight"
                    min="0"
                    value={weight}
                    onChange={(e) => {
                        showUpdateButton();
                        setWeight(e.target.value);
                    }}
                    onBlur={() => handleWeightBlur()}
                ></input>
                <label className={updatedInfo.weight && inputStyles['filled']}>
                    {isMetric ? "kg" : "lb"}
                </label>
            </div>
            {validationErrors.weight && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '2px' }}>{validationErrors.weight}</div>
            )}
            <div style={{position: "relative"}}>
                <span className="floating-label">Gender*</span>
                <select
                    className={inputStyles["styled-input-select"]}
                    id="gender"
                    style={{ width: "300px", borderColor: validationErrors.gender ? '#dc3545' : undefined }}
                    value={updatedInfo.gender}
                    onChange={handleChange}
                >
                    <option value="">Select a Gender</option>
                    {dropdowns.gender.values.map((type) => (
                        <option key={type.id} value={type.id}>
                            {type.display}
                        </option>
                    ))}
                </select>
                {validationErrors.gender && (
                    <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '2px' }}>{validationErrors.gender}</div>
                )}
            </div>
            <div style={{position: "relative"}}>
                <span className="floating-label">Critical Level</span>
                <select
                    className={inputStyles["styled-input-select"]}
                    id="criticalLevel"
                    style={{width:"300px"}}
                    value={updatedInfo.criticalLevel}
                    onChange={handleChange}
                >
                    <option value="">Select Critical Level</option>
                    {dropdowns.criticalLevel.values.map((type) => (
                        <option key={type.id} value={type.id}>
                            {type.display}
                        </option>
                    ))}
                </select>
            </div>
            <div className={inputStyles["styled-input-container"]} style={{width:"300px"}}>
                <textarea 
                    className={inputStyles["note-input"]}
                    style={{width:"300px"}}
                    id="notes"
                    type="text"
                    maxLength={250}
                    value={updatedInfo.notes}
                    onChange={handleChange}
                >
                </textarea>
                <label className={updatedInfo.notes && inputStyles['filled']}>
                    Notes
                </label>
                <span style={{fontSize:"10px", color:"#929395", marginLeft:"auto"}}>
                    {count}/250
                </span>
            </div>
            <div style={{display:"flex", columnGap:"10px"}}>
                <button variant='secondary' disabled={!showUpdateButtons} className={styles.SecondaryButton} onClick={handleCancel} >Cancel</button>
                <button variant='primary' disabled={!showUpdateButtons} className={styles.ModelButton} onClick={handleUpdate} >Save</button>
            </div>
        </div>
    );
}

export default HealthInfoDetail;